import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Divider from '@material-ui/core/Divider';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import wrap from 'wraptile';

import {
    DropFrameValues,
    FrameRateValues,
    IssueType,
    CaptionTypeDetails,
    ArtTypeDetails,
    AdBreakTypeDetails,
    IngestionBlockValues,
    AspectRatioValues,
    MisdeliveryTypesAffected,
    MisdeliveryDetails,
} from '../JiraMenu/JiraTypes';
import JiraIssueMultiSelect from '../JiraIssueCreate/JiraIssueMultiSelect';
import {QCJobItemModified} from '../../../QCManager/QCManagerTypes';
import {getSelectedContentPartnersNames, getLabPartnerName} from './IssueCreateDialogContainer';
import {getAdditionalInfo as getTitleAdditionalInfo, getSelectedJobsNames} from '../../../QCManager/QCManagerDialogs/AssignQCModal/AssignDialog';

interface IssueCreateDialogProps {
    dialogIsOpen: boolean;
    handleDialogClose: () => void;
    title?: string;
    contentPartner?: string;
    issueType: string;
    handleChangeIssueType: () => void;
    jobPriority: number;
    issueSpecificValues: string[];
    setIssueSpecificValues: (values: string[]) => void;
    assetTypesAffected: string[];
    setAssetTypesAffected: (values: string[]) => void;
    handleChangeSummary: () => void;
    handleChangeAssetsNumber: () => void;
    handleChangeDescription: () => void;
    handleChangeAspectRatio: () => void;
    aspectRatioValue: string;
    handleChangeDropFrame: () => void;
    dropFrameValue: string;
    handleChangeFrameRate: () => void;
    frameRateValue: string;
    handleChangeIngestionBlock: () => void;
    ingestionBlockValue: string;
    clearValuesOnClose: () => void;
    onSaveHandler: () => void;
    onMultipleSaveHandler: () => void;
    isSaveDisabled: boolean;
    isMultiple: boolean;
    data?: QCJobItemModified[];
    checkedItems?: number[];
    labPartner?: string;
}

interface IssueTypeOptionsProps {
    issueType: string;
    issueSpecificValues: string[];
    setIssueSpecificValues: (values: string[]) => void;
    assetTypesAffected: string[];
    setAssetTypesAffected: (values: string[]) => void;
    handleChangeIngestionBlock: () => void;
    ingestionBlockValue: string;
}

interface RenderCaptionSpecificControlsHandlersProps {
    issueType: string;
    aspectRatioValue: string;
    handleChangeAspectRatio: () => void;
    dropFrameValue: string;
    handleChangeDropFrame: () => void;
    frameRateValue: string;
    handleChangeFrameRate: () => void;
}

export default function IssueCreateDialog(props: IssueCreateDialogProps) {
    const {
        dialogIsOpen,
        handleDialogClose,
        title,
        contentPartner,
        issueType,
        handleChangeIssueType,
        jobPriority,
        issueSpecificValues,
        setIssueSpecificValues,
        assetTypesAffected,
        setAssetTypesAffected,
        handleChangeSummary,
        handleChangeDescription,
        handleChangeAssetsNumber,
        handleChangeAspectRatio,
        aspectRatioValue,
        handleChangeDropFrame,
        dropFrameValue,
        handleChangeFrameRate,
        frameRateValue,
        handleChangeIngestionBlock,
        ingestionBlockValue,
        clearValuesOnClose,
        onSaveHandler,
        onMultipleSaveHandler,
        isSaveDisabled,
        isMultiple,
        data = [],
        checkedItems = [],
        labPartner,
    } = props;
    
    const dialogClose = onDialogClose(handleDialogClose, clearValuesOnClose);
    const selectedItemsLength = checkedItems.length;
    const tooltipTitle = getSelectedJobsNames(data, checkedItems);
    const contentPartnersTooltipTitle = getSelectedContentPartnersNames(data, checkedItems);
    const additionalInfo = getTitleAdditionalInfo(selectedItemsLength, tooltipTitle, title);
    const additionalContentPartnersInfo = getTitleAdditionalInfo(
        selectedItemsLength,
        contentPartnersTooltipTitle,
        contentPartner,
    );
    const labPartnerName = getLabPartnerName(data, jobPriority);
    
    return (
        <>
            <Dialog
                className="dialog"
                open={dialogIsOpen}
                onClose={handleDialogClose}
            >
                <MuiDialogTitle
                    id="dialog-title"
                    disableTypography
                    className="dialog__title"
                >
                    <Typography variant="h6">
                        New Jira issue
                    </Typography>
                    <IconButton
                        aria-label="close"
                        className="close-button"
                        onClick={dialogClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiDialogTitle>
                <Divider className="popover__divider" />
                <div className="dialog__item">
                    Item: <span>{additionalInfo}</span>
                </div>
                <div className="dialog__priority">
                    Priority:
                    <span>{jobPriority}</span>
                </div>
                <div className="dialog__content-partners">
                    Content Partner:
                    <span>{additionalContentPartnersInfo}</span>
                </div>
                <div className="dialog__project-title">
                    Lab Partner:
                    <span>{labPartner || labPartnerName}</span>
                </div>
                <div className="dialog__main-block">
                    <div className="dialog__main-block__issue-type">
                        <FormControl variant="filled">
                            <InputLabel>
                        Issue type
                            </InputLabel>
                            <Select
                                displayEmpty
                                value={issueType}
                                onChange={handleChangeIssueType}
                            >
                                <MenuItem value={IssueType.Caption}>
                            Caption
                                </MenuItem>
                                <MenuItem value={IssueType.Metadata}>Metadata</MenuItem>
                                <MenuItem value={IssueType.Art}>Art</MenuItem>
                                <MenuItem value={IssueType.VideoAudio}>Video/Audio</MenuItem>
                                <MenuItem value={IssueType.AdBreak}>Ad-break</MenuItem>
                                <MenuItem value={IssueType.ApiJson}>API/JSON</MenuItem>
                                <MenuItem value={IssueType.Misdelivery}>Misdelivery</MenuItem>
                                <MenuItem value={IssueType.Other}>Other</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="dialog__main-block__summary">
                        <TextField
                            id="summary"
                            label="Summary"
                            variant="filled"
                            required
                            onChange={handleChangeSummary}
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                    </div>
                    <div className="dialog__main-block__description">
                        <TextField
                            id="description"
                            label="Description"
                            variant="filled"
                            multiline
                            required
                            rows={3}
                            onChange={handleChangeDescription}
                        />
                    </div>
                    {getIssueTypeOptions({
                        issueType,
                        issueSpecificValues,
                        setIssueSpecificValues,
                        assetTypesAffected,
                        setAssetTypesAffected,
                        handleChangeIngestionBlock,
                        ingestionBlockValue,
                    })}
                    <div className="dialog__main-block__summary">
                        <TextField
                            id="assetsAffectedNumber"
                            label="Number of assets affected"
                            variant="filled"
                            defaultValue={1}
                            type="number"
                            InputProps={{
                                inputProps: {
                                    min: 0
                                }
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            onChange={handleChangeAssetsNumber}
                        
                        />
                    </div>
                </div>
                {renderCaptionSpecificControls({
                    issueType,
                    aspectRatioValue,
                    handleChangeAspectRatio,
                    dropFrameValue,
                    handleChangeDropFrame,
                    frameRateValue,
                    handleChangeFrameRate,
                })}
                <Divider className="popover__divider" />
                <div className="dialog__buttons">
                    <div className="dialog__buttons_cancel">
                        <Button
                            onClick={dialogClose}
                            variant="text"
                        >
                        Cancel
                        </Button>
                    </div>
                    <div className="dialog__buttons_confirm">
                        <Button
                            onClick={isMultiple ? onMultipleSaveHandler : onSaveHandler}
                            disabled={isSaveDisabled}
                            variant="text"
                        >
                        Create Jira issue
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export const getIssueTypeOptions = (handlers: IssueTypeOptionsProps) => {
    const {
        issueType,
        issueSpecificValues,
        setIssueSpecificValues,
        assetTypesAffected,
        setAssetTypesAffected,
        handleChangeIngestionBlock,
        ingestionBlockValue,
    } = handlers;
    switch(issueType) {
    case IssueType.Caption:
        return (
            <JiraIssueMultiSelect
                placeholder="Issue details"
                options={Object.values(CaptionTypeDetails)}
                values={issueSpecificValues}
                setValues={setIssueSpecificValues}
            />
        );
    case IssueType.Art:
        return (
            <JiraIssueMultiSelect
                placeholder="Types affected"
                options={Object.values(ArtTypeDetails)}
                values={issueSpecificValues}
                setValues={setIssueSpecificValues}
            />
        );
    case IssueType.AdBreak:
        return (
            <JiraIssueMultiSelect
                placeholder="Issue details"
                options={Object.values(AdBreakTypeDetails)}
                values={issueSpecificValues}
                setValues={setIssueSpecificValues}
            />
        );
    case IssueType.ApiJson:
        return (
            <div className="dialog__additional-options">
                <FormControl variant="filled">
                    <InputLabel>
                        Ingestion block
                    </InputLabel>
                    <Select
                        value={ingestionBlockValue}
                        onChange={handleChangeIngestionBlock}
                    >
                        <MenuItem value={IngestionBlockValues.NotBlocked}>Not blocked</MenuItem>
                        <MenuItem value={IngestionBlockValues.Blocked}>Blocked</MenuItem>
                    </Select>
                </FormControl>
            </div>
        );
    case IssueType.Misdelivery:
        return (
            <>
                <JiraIssueMultiSelect
                    placeholder="Asset types affected"
                    options={Object.values(MisdeliveryTypesAffected)}
                    values={assetTypesAffected}
                    setValues={setAssetTypesAffected}
                />
                <JiraIssueMultiSelect
                    placeholder="Details"
                    options={Object.values(MisdeliveryDetails)}
                    values={issueSpecificValues}
                    setValues={setIssueSpecificValues}
                />
            </>
        );
    default:
        return null;
    }
};

export const renderCaptionSpecificControls = (handlers: RenderCaptionSpecificControlsHandlersProps) => {
    const {
        issueType,
        aspectRatioValue,
        handleChangeAspectRatio,
        dropFrameValue,
        handleChangeDropFrame,
        frameRateValue,
        handleChangeFrameRate,
    } = handlers;
    return issueType === 'Caption' ? (
        <div className="dialog__additional-info">
            <div className="dialog__additional-info_first-row">
                <FormControl
                    variant="filled"
                    className="dialog__additional-info_aspect-ratio"
                >
                    <InputLabel>Aspect ratio</InputLabel>
                    <Select
                        value={aspectRatioValue}
                        onChange={handleChangeAspectRatio}
                    >
                        <MenuItem value={AspectRatioValues.FirstValue}>4:3</MenuItem>
                        <MenuItem value={AspectRatioValues.SecondValue}>16:9</MenuItem>
                    </Select>
                </FormControl>
                <FormControl
                    variant="filled"
                    className="dialog__additional-info_frame-rate"
                >
                    <InputLabel>Framerate</InputLabel>
                    <Select
                        variant="filled"
                        value={frameRateValue}
                        onChange={handleChangeFrameRate}
                    >
                        <MenuItem value={FrameRateValues.FirstRate}>23.98</MenuItem>
                        <MenuItem value={FrameRateValues.SecondRate}>24</MenuItem>
                        <MenuItem value={FrameRateValues.ThirdRate}>25</MenuItem>
                        <MenuItem value={FrameRateValues.FourthRate}>29.97</MenuItem>
                        <MenuItem value={FrameRateValues.FifthRate}>30</MenuItem>
                        <MenuItem value={FrameRateValues.SixthRate}>50</MenuItem>
                        <MenuItem value={FrameRateValues.SeventhRate}>59.94</MenuItem>
                    </Select>
                </FormControl>
            </div>
            <FormControl
                variant="filled"
                className="dialog__additional-info_drop-frame"
            >
                <InputLabel>Drop frame</InputLabel>
                <Select
                    value={dropFrameValue}
                    onChange={handleChangeDropFrame}
                >
                    <MenuItem value={DropFrameValues.NDF}>NDF</MenuItem>
                    <MenuItem value={DropFrameValues.DF}>DF</MenuItem>
                </Select>
            </FormControl>
        </div>
    ) : null;
};

export const onDialogClose = wrap((handleDialogClose: () => void, clearValuesOnClose: () => void) => {
    clearValuesOnClose();
    handleDialogClose();
});

IssueCreateDialog.defaultProps = {
    title: '',
    data: [],
    checkedItems: [],
    contentPartner: '',
    labPartner: '',
};
